// Copyright © 2026 Alex Bruce. All rights reserved.
import React, { useState, useEffect, useRef, useCallback } from "react";
import type { Theme } from "../lib/theme";
import {
  loadImage, exportCrop, coverScale, clampOffset, cropRect,
  COVER_W, COVER_H,
} from "../lib/coverPhoto";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";
const MAX_ZOOM = 4;

interface Props {
  T: Theme;
  file: File;
  onCancel: () => void;
  onApply: (blob: Blob) => void;
}

/**
 * Position a photo inside the card's frame before it is uploaded.
 *
 * Drag to move, a slider to scale, and the frame is fixed at the card's own
 * 16:9 — so what you arrange here is exactly what the card shows. The image
 * can never be dragged far enough to uncover the frame; that constraint lives
 * in clampOffset, with tests, because a sliver of background at one edge is
 * the classic way this goes wrong.
 *
 * The crop is applied to the ORIGINAL pixels on the way out, not to a
 * preview, so zooming in costs no sharpness until you pass the photo's own
 * resolution.
 */
export default function CoverPhotoEditor({ T, file, onCancel, onApply }: Props) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [frameW, setFrameW] = useState(480);
  const frameH = Math.round(frameW * (COVER_H / COVER_W));

  const boxRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    let loaded: HTMLImageElement | null = null;
    loadImage(file)
      .then(i => {
        loaded = i;
        if (cancelled) { URL.revokeObjectURL(i.src); return; }
        setImg(i);
      })
      .catch(e => { if (!cancelled) setError(e.message || "Could not read that image."); });
    // loadImage hands over its object URL rather than revoking it, because
    // this component renders img.src. Releasing it is ours to do.
    return () => { cancelled = true; if (loaded) URL.revokeObjectURL(loaded.src); };
  }, [file]);

  // The frame follows the dialog so this still works on a laptop screen.
  useEffect(() => {
    const fit = () => setFrameW(Math.max(260, Math.min(520, window.innerWidth - 120)));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  const base = img ? coverScale(img.width, img.height, frameW, frameH) : 1;
  const scale = base * zoom;
  const dw = img ? img.width * scale : 0;
  const dh = img ? img.height * scale : 0;

  // Re-centre whenever the geometry changes under the current position.
  useEffect(() => {
    if (!img) return;
    setOffset(o => ({
      x: clampOffset(o.x || (frameW - dw) / 2, dw, frameW),
      y: clampOffset(o.y || (frameH - dh) / 2, dh, frameH),
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img, dw, dh, frameW, frameH]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!img) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    setOffset({
      x: clampOffset(d.ox + (e.clientX - d.px), dw, frameW),
      y: clampOffset(d.oy + (e.clientY - d.py), dh, frameH),
    });
  };
  const endDrag = () => { dragRef.current = null; };

  /** Zoom about the frame's centre, so the subject does not slide away. */
  const changeZoom = useCallback((next: number) => {
    if (!img) return;
    const z = Math.min(MAX_ZOOM, Math.max(1, next));
    const newScale = base * z;
    const ndw = img.width * newScale, ndh = img.height * newScale;
    setOffset(o => {
      const cx = frameW / 2 - o.x, cy = frameH / 2 - o.y;   // centre, in displayed px
      const ratio = newScale / scale;
      return {
        x: clampOffset(frameW / 2 - cx * ratio, ndw, frameW),
        y: clampOffset(frameH / 2 - cy * ratio, ndh, frameH),
      };
    });
    setZoom(z);
  }, [img, base, scale, frameW, frameH]);

  const apply = async () => {
    if (!img) return;
    setBusy(true);
    try {
      onApply(await exportCrop(img, cropRect(offset.x, offset.y, scale, frameW, frameH)));
    } catch (e: any) {
      setError(e?.message || "Could not process that image.");
      setBusy(false);
    }
  };

  const btn = (primary: boolean): React.CSSProperties => ({
    background: primary ? T.bgAccentSubtle : "none",
    border: `1px solid ${primary ? T.borderAccent + "55" : T.borderSecondary}`,
    color: primary ? T.borderAccent : T.textMuted,
    cursor: "pointer", padding: "8px 18px", borderRadius: 3, fontSize: 12,
    fontWeight: primary ? 700 : 400, fontFamily: FONT,
  });

  return (
    <div onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: T.bgModalBackdrop, zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, fontFamily: FONT, maxWidth: "100%" }}>

        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.borderSecondary}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.textSecondary }}>Position the cover photo</div>
          <div style={{ fontSize: 11, color: T.textWeak, marginTop: 3 }}>Drag to move, slide to zoom. The frame is the card.</div>
        </div>

        <div style={{ padding: "18px 20px" }}>
          {error ? (
            <div style={{ fontSize: 12, color: T.negative, padding: "24px 0", textAlign: "center", width: frameW }}>{error}</div>
          ) : (
            <div ref={boxRef}
              onPointerDown={onPointerDown} onPointerMove={onPointerMove}
              onPointerUp={endDrag} onPointerCancel={endDrag} onPointerLeave={endDrag}
              style={{ width: frameW, height: frameH, borderRadius: 6, overflow: "hidden", position: "relative",
                background: T.bgInput, cursor: img ? "grab" : "default", touchAction: "none", userSelect: "none" }}>
              {img && (
                <img src={img.src} alt="" draggable={false}
                  style={{ position: "absolute", left: offset.x, top: offset.y, width: dw, height: dh,
                    maxWidth: "none", display: "block", pointerEvents: "none" }} />
              )}
              {/* A rule of thirds, the one guide worth having without an editor. */}
              {img && [1, 2].map(i => (
                <React.Fragment key={i}>
                  <div style={{ position: "absolute", left: (frameW / 3) * i, top: 0, bottom: 0, width: 1, background: "#ffffff22" }} />
                  <div style={{ position: "absolute", top: (frameH / 3) * i, left: 0, right: 0, height: 1, background: "#ffffff22" }} />
                </React.Fragment>
              ))}
            </div>
          )}

          {!error && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, width: frameW }}>
              <Icon.Search size={12} color={T.textWeak} />
              <input type="range" min={1} max={MAX_ZOOM} step={0.01} value={zoom}
                aria-label="Zoom"
                onChange={e => changeZoom(parseFloat(e.target.value))}
                style={{ flex: 1, accentColor: T.borderAccent, cursor: "pointer" }} />
              <span style={{ fontSize: 11, color: T.textWeak, width: 34, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {zoom.toFixed(1)}×
              </span>
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.borderSecondary}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={btn(false)}>Cancel</button>
          <button onClick={apply} disabled={!img || busy || !!error}
            style={{ ...btn(true), opacity: !img || busy || error ? 0.5 : 1 }}>
            {busy ? "Saving…" : "Use this photo"}
          </button>
        </div>
      </div>
    </div>
  );
}
